import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiHome, FiHeart, FiMenu, FiX } = FiIcons;

function Navigation() {
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectedCount, setSelectedCount] = React.useState(0);
  const location = useLocation();

  React.useEffect(() => {
    const updateCount = () => {
      const stored = JSON.parse(localStorage.getItem('selectedPoses') || '[]');
      setSelectedCount(stored.length);
    };
    updateCount();
    window.addEventListener('favoritesUpdated', updateCount);
    return () => window.removeEventListener('favoritesUpdated', updateCount);
  }, []);

  const navigationItems = [
    { path: '/', label: 'Home', icon: FiHome },
    { path: '/selection', label: 'My Selection', icon: FiHeart },
  ];

  const isActivePath = (path) => {
    if (path === '/') return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Mobile Menu Button */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className="fixed top-6 left-6 z-50 md:hidden p-3 bg-white rounded-full shadow-lg border border-brand-bg-dark"
      >
        <SafeIcon icon={isOpen ? FiX : FiMenu} className="text-lg text-brand-primary" />
      </motion.button>

      {/* Desktop Navigation */}
      <nav className="hidden md:block">
        <div className="bg-white rounded-2xl shadow-lg border border-brand-bg-dark p-2 mb-8">
          <div className="flex items-center space-x-2">
            {navigationItems.map((item) => (
              <Link key={item.path} to={item.path}>
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`flex items-center px-4 py-3 rounded-xl transition-all duration-300 ${
                    isActivePath(item.path)
                      ? 'bg-brand-primary text-white'
                      : 'text-slate-600 hover:text-brand-primary hover:bg-brand-bg'
                  }`}
                >
                  <SafeIcon icon={item.icon} className="mr-2" />
                  <span className="font-medium text-sm">{item.label}</span>
                  {item.label === 'My Selection' && selectedCount > 0 && (
                    <span className="ml-2 bg-brand-accent text-white text-xs px-2 py-1 rounded-full">
                      {selectedCount}
                    </span>
                  )}
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </nav>

      {/* Mobile Navigation */}
      {isOpen && (
        <motion.div
          initial={{ x: -300, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: -300, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="fixed inset-0 z-40 md:hidden"
        >
          <div className="fixed inset-0 bg-black/50" onClick={() => setIsOpen(false)} />
          <motion.div
            initial={{ x: -300 }}
            animate={{ x: 0 }}
            exit={{ x: -300 }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed left-0 top-0 h-full w-72 bg-white shadow-2xl border-r border-brand-bg-dark"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-lg font-bold text-brand-primary">Navigation</h2>
                <button onClick={() => setIsOpen(false)}>
                  <SafeIcon icon={FiX} className="text-lg text-slate-600" />
                </button>
              </div>
              <div className="space-y-2">
                {navigationItems.map((item) => (
                  <Link key={item.path} to={item.path} onClick={() => setIsOpen(false)}>
                    <motion.div
                      whileHover={{ scale: 1.02, x: 4 }}
                      whileTap={{ scale: 0.98 }}
                      className={`flex items-center px-4 py-4 rounded-xl transition-all duration-300 ${
                        isActivePath(item.path)
                          ? 'bg-brand-primary text-white'
                          : 'text-slate-600 hover:text-brand-primary hover:bg-brand-bg'
                      }`}
                    >
                      <SafeIcon icon={item.icon} className="mr-3 text-lg" />
                      <div className="flex-1">
                        <span className="font-medium">{item.label}</span>
                      </div>
                      {item.label === 'My Selection' && selectedCount > 0 && (
                        <span className="bg-brand-accent text-white text-sm px-3 py-1 rounded-full">
                          {selectedCount}
                        </span>
                      )}
                    </motion.div>
                  </Link>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </>
  );
}

export default Navigation;